import { JSX, useCallback, useEffect, useState } from "react";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import ImageSlideshow from "lib/components/image/ImageSlideshow";
import ImageView from "lib/components/image/ImageView";
import PictureCaptureView from "lib/components/picture/PictureCaptureView";

import { compress } from "utils/ImageUtils";
import { deleteElement } from "utils/Utils";

import {
	createResource,
	getResourceContent,
	updateResourceContent
} from "resource/ResourceService";

import { ImageField } from "../FieldService";
import {
	InformationRight,
	ReportFieldRights
} from "core/blueprint/trajectory/TrajectoryBlueprintService";

export type ImageInputProps = {
	field: ImageField;
	rights: ReportFieldRights;
	editable: boolean;

	onChange: (uuid: string, value: string) => void;
};

export default function ImageInput(
	props: Readonly<ImageInputProps>
): JSX.Element {

	const [images, setImages] = useState<Array<string>>([]);
	const [uploading, setUploading] = useState<number[]>([]);
	const [slideshow, setSlideshow] = useState<number>();

	// We only load the images on the initial render.
	useEffect(() => {
		Promise.all(
			props.field.value.map(id =>
				getResourceContent(id).then(URL.createObjectURL)
			)
		).then(setImages);
	}, []);

	useEffect(() => {
		return () => {
			images.forEach(URL.revokeObjectURL);
		};
	}, [images]);

	async function uploadImage(image: Blob, index: number): Promise<void> {
		setUploading(previous => previous.concat(index));
		const file = await compress(image);

		const data = new FormData();
		data.append("file", file);
		createResource({
			name: file.name,
			mediaType: file.type,
			userRights: []
		})
			.then(location => {
				const id = location.substring(location.lastIndexOf("/") + 1);
				return Number.parseInt(id, 10);
			})
			.then(id =>
				updateResourceContent(id, data).then(() => {
					const newValue = props.field.value.concat(id);
					props.onChange(props.field.uuid, JSON.stringify(newValue));
				})
			)
			.catch(() => {
				throw new Error("failed to create resource");
			})
			.finally(() =>
				setUploading(previous => previous.filter(id => id !== index))
			);
	}

	const changeImage = useCallback(
		(image: Blob): void => {
			setImages(previous => previous.concat(URL.createObjectURL(image)));
			uploadImage(image, images.length);
		},
		[props.onChange, props.field.uuid, props.field.value, images]
	);

	const deleteImage = useCallback(
		(index: number): void => {
			setImages(previous => {
				URL.revokeObjectURL(previous[index]);
				return deleteElement(index, previous);
			});

			const newValue = deleteElement(index, props.field.value);
			props.onChange(props.field.uuid, JSON.stringify(newValue));
		},
		[props.onChange, props.field.uuid, props.field.value]
	);

	function toId(uuid: string): string {
		const separatorIndex = uuid.indexOf("#");
		return separatorIndex === -1 ? uuid : uuid.substring(0, separatorIndex);
	}

	return (
		<Grid item>
			<Grid container direction="column" spacing={1}>
				<Grid item>
					<Typography>{props.field.name}</Typography>
					{props.editable &&
						props.rights[toId(props.field.uuid)][
							InformationRight.WRITE
						] && <PictureCaptureView onCapture={changeImage} />}
				</Grid>

				<Grid item container spacing={2}>
					{images.map((image, index) => (
						<Grid item key={index}>
							<ImageView
								url={image}
								loading={uploading.includes(index)}
								disabled={
									!props.editable ||
									props.rights[toId(props.field.uuid)][
										InformationRight.WRITE
									] === false
								}
								onClick={() => setSlideshow(index)}
								onDelete={() => deleteImage(index)}
							/>
						</Grid>
					))}
				</Grid>

				{slideshow !== undefined && (
					<ImageSlideshow
						images={images}
						index={slideshow}
						onClose={() => setSlideshow(undefined)}
					/>
				)}
			</Grid>
		</Grid>
	);
}
